import { colors } from '@ui/colors'
import useDateFormat from '@ui/hooks/useDateFormat'
import React, {
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Platform,
  Pressable,
  Text,
  TextInput,
  TextInputProps,
  View,
} from 'react-native'
import { useSharedValue, withSpring } from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import InputAction from '../Input/InputAction'
import InputContainer from '../Input/InputContainer'
import InputTextEnd from '../Input/InputTextEnd'
import { InputDateProps } from '../Input/types'
import DatePickerModal from './DatePickerModal'
import parseDateInput from './parseDateInput'
import { inputDateStyles } from './styles'
import { useDebouncedCallback } from 'use-debounce'
import { dateToString, getPlaceholderFormat } from './utils'
import InputLabelTop from '@ui/forms/Input/InputLabelTop'

const HIT_SLOP = {
  top: 10,
  bottom: 10,
  left: 10,
  right: 10,
}

const getPressableColor = ({
  disabled,
  textValue,
}: {
  disabled?: boolean
  textValue: string
}) => {
  if (disabled) return colors.black50
  if (!textValue) return colors.black30
  return colors.black
}

type DateInputActionProps = {
  action: React.ReactElement<React.ComponentProps<typeof InputAction>>
  onPress: () => void
  disabled?: boolean
}

const DateInputAction = ({ action, onPress, disabled }: DateInputActionProps) => {
  if (action.props.onPress) {
    return action
  }

  return (
    <View className="relative z-10 shrink-0">
      <InputAction {...action.props} onPress={onPress} disabled={disabled} />
    </View>
  )
}

const InputDate = forwardRef<HTMLInputElement, InputDateProps>(
  (
    {
      label,
      labelVariant = 'inline',
      placeholder = '',
      icon,
      required,
      onFocus,
      onBlur,
      className,
      center,
      style,
      errorMessage,
      successMessage,
      isSuccess,
      action,
      textEnd,
      hideIndicator,
      value,
      defaultDate,
      onChange,
      min,
      max,
      enableEdit = true,
      formatDate: formatDateProp,
      ...rest
    },
    ref
  ) => {
    const success = useSharedValue(0)
    const error = useSharedValue(0)
    const focus = useSharedValue(0)
    const disabled = useSharedValue(0)
    const readonly = useSharedValue(0)
    const localRef = useRef<HTMLInputElement>(null)
    const webDateInputRef = useRef<HTMLInputElement | null>(null)
    const textInputRef = useRef<TextInput>(null)
    const [isEditMode, setIsEditMode] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [textValue, setTextValue] = useState(value || '')

    const handleEditPress = useCallback(() => {
      setIsEditMode(true)
      setTimeout(() => {
        textInputRef.current?.focus()
      }, 100)
    }, [])

    const handleCalendarPress = useCallback(() => {
      setIsEditMode(false)
      setIsModalOpen(true)
    }, [])

    const updateDate = useCallback(
      (text: string) => {
        try {
          const parsedDate = parseDateInput(text)

          if (parsedDate) {
            onChange?.(parsedDate)
          }
        } catch (e) {
          // date not valid ignore
        }
      },
      [onChange]
    )

    const debouncedOnEditText = useDebouncedCallback(updateDate, 300)

    const handleTextChange = useCallback(
      (text: string) => {
        debouncedOnEditText(text)
        setTextValue(text)
      },
      [debouncedOnEditText]
    )

    const handleTextSubmit = useCallback(() => {
      try {
        const parsedDate = parseDateInput(textValue)

        if (parsedDate) {
          onChange?.(parsedDate)
        }
      } catch (e) {
        setTextValue(value || '')
      }
    }, [textValue, onChange, value])

    const handleTextBlur = useCallback(() => {
      handleTextSubmit()
    }, [handleTextSubmit])

    const handleTextFocus = useCallback<NonNullable<TextInputProps['onFocus']>>(
      (e) => {
        focus.set(
          withSpring(1, {
            overshootClamping: true,
          })
        )
        onFocus?.(e)
      },
      [onFocus, focus]
    )

    const handleDateSelect = useCallback(() => {
      setIsModalOpen(true)
    }, [])

    const setWebDateInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        webDateInputRef.current = node
        localRef.current = node
        if (typeof ref === 'function') {
          ref(node)
          return
        }
        if (ref) {
          ref.current = node
        }
      },
      [ref]
    )

    const openDatePicker = useCallback(() => {
      if (rest.disabled) {
        return
      }

      if (Platform.OS === 'web') {
        const input = webDateInputRef.current
        if (!input) {
          return
        }

        try {
          if (typeof input.showPicker === 'function') {
            void input.showPicker()
            return
          }
        } catch {
          // showPicker can fail outside a user gesture on some browsers
        }

        input.focus()
        input.click()
        return
      }

      setIsModalOpen(true)
    }, [rest.disabled])

    const handleModalClose = useCallback(() => {
      setIsModalOpen(false)
    }, [])

    const handleModalChange = useCallback(
      (date: Date) => {
        onChange?.(date)
      },
      [onChange]
    )

    const dateValue = useMemo(() => {
      // defaultDate prop has priority over react-hook-form defaultValues
      if (defaultDate && !value) return defaultDate
      if (!value) return null

      // Try to parse the value as a date string
      const parsed = parseDateInput(value)
      if (parsed) {
        return parsed
      }

      // Fallback to direct Date constructor if parsing fails
      try {
        return new Date(value)
      } catch {
        return null
      }
    }, [value, defaultDate])

    const localeFormatDate = useDateFormat({
      dateStyle: 'medium',
    })
    const formatDate = formatDateProp ?? localeFormatDate

    // Handle text value when value changes or when using default date
    useEffect(() => {
      if (isEditMode) return

      if (!value) {
        return
      }
      try {
        // Try to parse the value as a date string first
        const parsedDate = parseDateInput(value)
        if (parsedDate) {
          const displayString = formatDate(parsedDate)
          // FIXME: We should think if its possible to rewrite this useEffect to not use setTextValue
          setTextValue(displayString)
        } else {
          // Fallback to direct string value if parsing fails
          setTextValue(value)
        }
      } catch (error) {
        setTextValue(value)
      }
    }, [value, defaultDate, isEditMode, formatDate])

    const placeholderFormat = useMemo(() => {
      return placeholder || getPlaceholderFormat(formatDate)
    }, [placeholder, formatDate])

    const editAction = enableEdit ? (
      <InputAction
        icon={isEditMode ? 'calendar-event' : 'pencil'}
        onPress={isEditMode ? handleCalendarPress : handleEditPress}
        disabled={rest.disabled}
      />
    ) : null

    const onChangeText: React.ChangeEventHandler<HTMLInputElement> =
      useCallback(
        (e) => {
          try {
            const date = new Date(e.target.value)
            if (date) {
              onChange?.(date)
            }
          } catch (e) {
            console.error('Invalid date', e)
          }
        },
        [onChange]
      )

    useEffect(() => {
      success.set(withSpring(successMessage || isSuccess ? 1 : 0))
      error.set(withSpring(errorMessage ? 1 : 0))
      disabled.set(withSpring(rest.disabled ? 1 : 0))
      readonly.set(withSpring(rest.readonly ? 1 : 0))
    }, [
      success,
      successMessage,
      isSuccess,
      error,
      errorMessage,
      disabled,
      readonly,
      rest.disabled,
      rest.readonly,
    ])

    const hasCalendarAction = !!action

    const dateDisplayClassName = cn(
      inputDateStyles.input.base,
      rest.disabled && inputDateStyles.input.disabled,
      center && inputDateStyles.input.center
    )

    const dateDisplayText = (
      <Text
        className={cn(
          inputDateStyles.text.base,
          rest.disabled && inputDateStyles.text.disabled,
          !textValue && inputDateStyles.text.placeholder
        )}
        style={{
          color: getPressableColor({
            disabled: !!rest.disabled,
            textValue,
          }),
        }}
      >
        {textValue || placeholderFormat}
      </Text>
    )

    return (
      <View className="flex w-full flex-col gap-3">
        {labelVariant === 'top' && (
          <InputLabelTop
            label={label}
            required={required}
            success={success}
            error={error}
            disabled={disabled}
            readonly={readonly}
            focus={focus}
          />
        )}
        <InputContainer
          success={success}
          error={error}
          style={style}
          hideIndicator={hideIndicator}
          className={cn(
            inputDateStyles.container.base,
            rest.disabled && inputDateStyles.container.disabled,
            className
          )}
          onLabelPress={() => {
            if (ref && 'current' in ref) ref.current?.focus()
            localRef.current?.focus()
          }}
          label={label}
          labelVariant={labelVariant}
          icon={icon}
          focus={focus}
          disabled={disabled}
          successMessage={successMessage}
          errorMessage={errorMessage}
          required={required}
          center={center}
          readonly={readonly}
        >
          {Platform.select({
            web: (
              <input
                type="date"
                ref={setWebDateInputRef}
                onFocus={(e) => {
                  focus.set(
                    withSpring(1, {
                      overshootClamping: true,
                    })
                  )
                  onFocus?.(e)
                }}
                min={min ? dateToString(min) : undefined}
                max={max ? dateToString(max) : undefined}
                placeholder={placeholder}
                onBlur={(e) => {
                  focus.set(
                    withSpring(0, {
                      overshootClamping: true,
                    })
                  )
                  onBlur?.(e)
                }}
                autoComplete="off"
                className={cn(
                  'relative z-0 h-full min-w-0 flex-1 bg-transparent px-6 py-4 pr-4 font-text-semibold text-[0.875rem] tracking-[-0.28] outline-none placeholder:color-black/30',
                  rest.disabled && 'color-black/25 placeholder:color-black/10',
                  center && 'px-0 text-center'
                )}
                disabled={!!rest.disabled}
                defaultValue={value}
                onChange={onChangeText}
              />
            ),
            default: isEditMode ? (
              <TextInput
                hitSlop={HIT_SLOP}
                ref={textInputRef}
                value={textValue}
                onChangeText={handleTextChange}
                onSubmitEditing={handleTextSubmit}
                onBlur={handleTextBlur}
                onFocus={handleTextFocus}
                placeholder={placeholderFormat}
                autoComplete="off"
                className={cn(
                  inputDateStyles.input.base,
                  rest.disabled && inputDateStyles.input.disabled,
                  center && inputDateStyles.input.center
                )}
                style={{
                  color: rest.disabled ? colors.black50 : colors.black,
                }}
                editable={!rest.disabled}
              />
            ) : hasCalendarAction ? (
              <View className={dateDisplayClassName}>{dateDisplayText}</View>
            ) : (
              <Pressable
                onPress={handleDateSelect}
                className={dateDisplayClassName}
                disabled={!!rest.disabled}
                hitSlop={HIT_SLOP}
              >
                {dateDisplayText}
              </Pressable>
            ),
          })}
          {!!textEnd && <InputTextEnd text={textEnd} />}
          {Platform.OS !== 'web' && editAction}
          {isValidElement<React.ComponentProps<typeof InputAction>>(action) ? (
            <DateInputAction
              action={action}
              onPress={openDatePicker}
              disabled={rest.disabled}
            />
          ) : (
            !!action && action
          )}
        </InputContainer>
        <DatePickerModal
          open={isModalOpen}
          close={handleModalClose}
          value={dateValue}
          onChange={handleModalChange}
          min={min}
          max={max}
        />
      </View>
    )
  }
)

InputDate.displayName = 'InputDate'

export default InputDate
